import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Package, AlertTriangle, Check, ArrowLeft, Edit3, Image as ImageIcon } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import { usePhotoLightbox } from '../components/PhotoLightbox';
import axios from 'axios';
import { openRazorpayCheckout } from '../lib/razorpay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYMENT_PROVIDER = (process.env.REACT_APP_PAYMENT_PROVIDER || 'stripe').toLowerCase();

const Payment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  // After Razorpay confirms server-side, we show a full-screen success overlay
  // so the user never sees the "AUTHORIZE PAYMENT" form again during the
  // brief router transition to /receipt.
  const [paidOverlay, setPaidOverlay] = useState(false);
  const { open: openPhoto, gallery: photoGallery } = usePhotoLightbox();

  useEffect(() => {
    if (!bookingId) {
      navigate('/');
      return;
    }
    fetchBooking();
  }, [bookingId, navigate]);

  const fetchBooking = async () => {
    try {
      const uid = localStorage.getItem('user_id') || '';
      const response = await axios.get(`${API}/bookings/${bookingId}`, { params: { user_id: uid } });
      setBooking(response.data);
    } catch (err) {
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!disclaimerAccepted) {
      setError('You must accept the disclaimer before proceeding to payment');
      return;
    }
    setProcessing(true);
    setError('');

    try {
      if (PAYMENT_PROVIDER === 'razorpay') {
        await openRazorpayCheckout({
          booking,
          purpose: 'booking',
          onSuccess: () => {
            // Mark UI as paid before navigating so the Payment form never
            // flashes again during the router transition to the receipt page.
            setPaidOverlay(true);
            // Hand off to the receipt page using the razorpay order id.
            navigate(`/receipt?provider=razorpay&booking_id=${bookingId}`);
          },
          onDismiss: () => {
            setProcessing(false);
          },
        });
      } else {
        const originUrl = window.location.origin;
        const response = await axios.post(`${API}/payments/checkout`, {
          booking_id: bookingId,
          origin_url: originUrl
        });
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to initiate payment';
      if (detail !== 'Payment cancelled') {
        setError(detail);
      }
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="theme-sunset flex items-center justify-center">
        <div className="text-center">
          <div className="loading-pulse font-mono text-lg">LOADING...</div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="theme-sunset flex items-center justify-center">
        <div className="text-center">
          <p className="font-mono text-red-600">Booking not found</p>
        </div>
      </div>
    );
  }

  const baseFare = 500;
  const bags = booking.luggage_details.bags || [];
  const hasBagsArray = bags.length > 0;

  // Pricing model (Feb 2026): base ₹500 + per-bag (weight × ₹65) + 18% GST.
  // No handling surcharge, no transport line, no "other charges".
  let bagsBreakdown = [];
  if (hasBagsArray) {
    bagsBreakdown = bags.map((b, i) => ({
      index: i + 1,
      type: (b.bag_type || 'bag').toUpperCase(),
      weight_kg: b.weight || 0,
      weight: (b.weight || 0) * 65,
    }));
  }
  const legacyWeightCharge = (booking.luggage_details.weight || 0) * 65;

  if (paidOverlay) {
    return (
      <div className="theme-sunset flex items-center justify-center px-4" data-testid="payment-success-overlay">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#FF5A1F] flex items-center justify-center">
            <Check size={32} strokeWidth={3} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-[#1A1A1A] mb-3">Payment received</h2>
          <p className="text-[#525252] text-sm">Generating your receipt…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-sunset p-4 md:p-8" style={{ overflowX: 'hidden' }}>
      <div className="max-w-2xl mx-auto min-w-0">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BrandMark size={36} />
          </div>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
                PAYMENT
              </h1>
              <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">AUTHORIZE TRANSACTION</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                data-testid="payment-back-btn"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary px-3 py-1.5 flex items-center gap-1.5 text-[11px]"
              >
                <ArrowLeft size={12} />
                BACK
              </button>
              <button
                data-testid="payment-edit-booking-btn"
                onClick={() => navigate(`/luggage-form?edit=${bookingId}`)}
                className="btn-secondary px-3 py-1.5 flex items-center gap-1.5 text-[11px] hover:bg-[#FF4500] hover:text-black"
              >
                <Edit3 size={12} />
                EDIT BOOKING
              </button>
            </div>
          </div>
        </div>

        {/* Booking Summary */}
        <div className="card-brutalist p-6 mb-6" data-testid="booking-summary">
          <h2 className="text-lg font-bold uppercase mb-4 pb-2 border-b-2 border-black" style={{ fontFamily: 'IBM Plex Mono' }}>
            BOOKING DETAILS
          </h2>
          <div className="space-y-2">
            <div className="data-row">
              <span className="data-label">BAGGAGE TAG</span>
              <span className="data-value" data-testid="baggage-tag-display">{booking.baggage_tag}</span>
            </div>
            <div className="data-row">
              <span className="data-label">CUSTOMER</span>
              <span className="data-value">{booking.customer_name}</span>
            </div>
            <div className="data-row">
              <span className="data-label">ROUTE</span>
              <span className="data-value text-xs">{booking.pickup_location || 'PICKUP'} → {booking.delivery_location}</span>
            </div>
            <div className="data-row">
              <span className="data-label">LUGGAGE</span>
              <span className="data-value">
                {hasBagsArray ? `${bags.length} BAG${bags.length !== 1 ? 'S' : ''}` : `${booking.luggage_details.quantity} × ${(booking.luggage_details.luggage_type || 'BAG').toUpperCase()}`}
              </span>
            </div>
            <div className="data-row">
              <span className="data-label">TOTAL WEIGHT</span>
              <span className="data-value">{booking.luggage_details.weight} KG</span>
            </div>
          </div>

          {/* Per-bag breakdown */}
          {hasBagsArray && (
            <div className="mt-4 pt-4 border-t-2 border-dashed border-gray-300">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-3">
                BAGS DETAIL — VERIFY BEFORE PAYMENT
              </div>
              <div className="space-y-3" data-testid="payment-bags-detail">
                {bags.map((bag, idx) => (
                  <div key={idx} className="border-2 border-black p-3 bg-gray-50" data-testid={`payment-bag-${idx}`}>
                    <div className="flex items-center justify-between mb-2 pb-1 border-b-2 border-dashed border-gray-300">
                      <div className="font-mono font-black text-sm">BAG #{idx + 1}</div>
                      <div className="px-2 py-0.5 bg-black text-white text-[10px] font-mono uppercase">
                        {(bag.bag_type || 'BAG').toUpperCase()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                      <div>
                        <div className="data-label text-[10px]">COLOR</div>
                        <div className="font-bold uppercase">{bag.color || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label text-[10px]">COMPANY/BRAND</div>
                        <div className="font-bold uppercase">{bag.brand || '—'}</div>
                      </div>
                      <div>
                        <div className="data-label text-[10px]">WEIGHT</div>
                        <div className="font-bold">{bag.weight} KG</div>
                      </div>
                    </div>
                    {(bag.images || []).length > 0 && (
                      <div className="mt-3 pt-2 border-t border-dashed border-gray-300">
                        <div className="data-label text-[10px] flex items-center gap-1 mb-1">
                          <ImageIcon size={9} />
                          PHOTOS ({bag.images.length})
                        </div>
                        <div className="grid grid-cols-4 gap-1">
                          {bag.images.map((img, imgIdx) => (
                            <button
                              key={imgIdx}
                              type="button"
                              onClick={() => openPhoto(bag.images, imgIdx)}
                              data-testid={`payment-bag-${idx}-photo-${imgIdx}`}
                              className="block border border-black overflow-hidden hover:opacity-80 cursor-zoom-in"
                              title="Click to enlarge"
                            >
                              <img src={img} alt={`Bag ${idx + 1} photo ${imgIdx + 1}`} className="w-full h-14 object-cover" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-orange-600 font-mono mt-3 italic">
                Notice a mistake? Click <strong>EDIT BOOKING DETAILS</strong> above to make changes.
              </p>
            </div>
          )}
        </div>

        {/* Price Breakdown */}
        <div className="price-breakdown mb-6" data-testid="price-breakdown">
          <h2 className="text-lg font-bold uppercase mb-4 pb-2 border-b-2 border-black" style={{ fontFamily: 'IBM Plex Mono' }}>
            COST BREAKDOWN
          </h2>
          <div className="space-y-1">
            <div className="price-row">
              <div className="flex flex-col">
                <span>Base Fare</span>
                <span className="text-[10px] font-mono uppercase text-gray-500 italic mt-0.5">
                  Non-refundable once luggage picked up
                </span>
              </div>
              <span>₹{baseFare.toFixed(2)}</span>
            </div>
            {hasBagsArray ? (
              <>
                {bagsBreakdown.map((b) => (
                  <div key={b.index} className="py-2 border-t border-gray-200">
                    <div className="price-row font-bold text-sm">
                      <span>Bag {b.index} — {b.type}</span>
                      <span>₹{b.weight.toFixed(2)}</span>
                    </div>
                    <div className="text-xs text-gray-600 ml-2 space-y-0.5 mt-1 font-mono">
                      <div className="flex justify-between"><span>· {b.weight_kg} kg × ₹65</span><span>₹{b.weight.toFixed(2)}</span></div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="price-row">
                <span>Weight Charge ({booking.luggage_details.weight} kg × ₹65)</span>
                <span>₹{legacyWeightCharge.toFixed(2)}</span>
              </div>
            )}

            {/* Other charges — only shown for legacy bookings that still have a non-zero value */}
            {Number(booking.other_charges || 0) > 0 && (
              <div className="price-row" data-testid="other-charges-row">
                <span>Other Charges</span>
                <span data-testid="other-charges-value">
                  ₹{Number(booking.other_charges).toFixed(2)}
                </span>
              </div>
            )}

            {/* GST */}
            <div className="price-row" data-testid="gst-row">
              <span>GST (18%)</span>
              <span data-testid="gst-value">
                ₹{Number(booking.gst_amount ?? 0).toFixed(2)}
              </span>
            </div>

            {/* Insurance premium — only shown if any bag opted in */}
            {Number(booking.insurance_premium || 0) > 0 && (
              <div className="price-row" data-testid="insurance-row">
                <div className="flex flex-col">
                  <span>Insurance Premium (4%)</span>
                  <span className="text-[10px] font-mono uppercase text-gray-500 italic mt-0.5">
                    {(() => {
                      const insuredBags = (booking.luggage_details?.bags || [])
                        .map((b, i) => (b.insurance_opted ? { i: i + 1, dv: Number(b.declared_value || 0) } : null))
                        .filter(Boolean);
                      if (insuredBags.length === 0) return 'Opted-in bag coverage';
                      const total = insuredBags.reduce((a, b) => a + b.dv, 0);
                      return `Covering ${insuredBags.length} bag${insuredBags.length === 1 ? '' : 's'} · ₹${total.toLocaleString('en-IN')} declared`;
                    })()}
                  </span>
                </div>
                <span data-testid="insurance-value">
                  ₹{Number(booking.insurance_premium).toFixed(2)}
                </span>
              </div>
            )}

            <div className="price-row price-total">
              <span>TOTAL AMOUNT</span>
              <span data-testid="total-amount">₹{booking.amount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Disclaimer Section */}
        <div className="card-brutalist p-6 mb-6 border-l-8" style={{ borderLeftColor: '#FF4500' }} data-testid="disclaimer-section">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-black">
            <AlertTriangle size={20} color="#FF4500" />
            <h2 className="text-lg font-bold uppercase" style={{ fontFamily: 'IBM Plex Mono' }}>
              CUSTOMER DECLARATION
            </h2>
          </div>
          <p className="text-sm font-mono mb-4 text-gray-700">
            By proceeding to payment, I confirm and declare that:
          </p>
          <ul className="space-y-2 mb-4 text-sm" data-testid="disclaimer-points">
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold mt-0.5">▸</span>
              <span>My bags do <strong>NOT contain any valuables</strong> such as gold, silver, platinum, jewellery, cash, or high-value electronics.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold mt-0.5">▸</span>
              <span>My bags do <strong>NOT contain any prohibited items</strong> including drugs, narcotics, illegal substances, weapons, ammunition, or hazardous materials.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold mt-0.5">▸</span>
              <span>My bags do <strong>NOT contain any flammable, explosive, or perishable goods</strong>.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold mt-0.5">▸</span>
              <span><strong>All my bags are properly locked</strong> and the contents are securely packed.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold mt-0.5">▸</span>
              <span>The information provided about my luggage (type, weight, dimensions, color, brand) is <strong>true and accurate</strong>.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-600 font-bold mt-0.5">▸</span>
              <span>I understand that LUGGAGEFREEJOURNEY <strong>shall not be liable</strong> for any loss arising from undeclared valuables or prohibited items.</span>
            </li>
          </ul>
          
          <label className="flex items-start gap-3 p-4 border-2 border-black cursor-pointer hover:bg-gray-50 transition-colors" data-testid="disclaimer-checkbox-label">
            <div
              className={`w-6 h-6 border-2 border-black flex items-center justify-center flex-shrink-0 mt-0.5 ${disclaimerAccepted ? 'bg-[#FF4500]' : 'bg-white'}`}
            >
              {disclaimerAccepted && <Check size={16} strokeWidth={3} />}
            </div>
            <input
              data-testid="disclaimer-checkbox"
              type="checkbox"
              checked={disclaimerAccepted}
              onChange={(e) => setDisclaimerAccepted(e.target.checked)}
              className="hidden"
            />
            <span className="text-sm font-mono font-bold uppercase">
              I HAVE READ AND ACCEPT THE ABOVE DECLARATION
            </span>
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <p data-testid="payment-error" className="text-red-600 text-sm font-mono p-4 border-2 border-red-600 bg-red-50">
              {error}
            </p>
          </div>
        )}

        {/* Payment Button */}
        <button
          data-testid="authorize-payment-btn"
          onClick={handlePayment}
          disabled={processing || !disclaimerAccepted}
          className="btn-primary w-full py-6 text-xl flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CreditCard size={28} />
          {processing ? 'PROCESSING...' : 'AUTHORIZE PAYMENT'}
        </button>

        {!disclaimerAccepted && (
          <p className="text-center text-xs text-orange-600 mt-3 font-mono uppercase">
            ↑ Accept the declaration above to enable payment
          </p>
        )}

        <p className="text-center text-xs text-gray-600 mt-4 font-mono">
          SECURE PAYMENT POWERED BY RAZORPAY
        </p>
      </div>
      {photoGallery}
    </div>
  );
};

export default Payment;